import {Tab} from "@headlessui/react";
import DayJS from "dayjs";
import React, {useState, useEffect, useMemo} from "react";
import Toast from "react-hot-toast";
import Box from "../../components/common/Box";
import Layout from "../../components/common/Layout";
import API from "../../api";
import Select from "../../components/common/Select";
import Stat from "../../components/common/Stat";
import {Skeleton} from "../../components/ui/skeleton";
import CommonFunctions from "../../lib/CommonFunctions";
import SettingsDropdownList from "../settings/_components/SettingsDropdownList";

const returnTabButtonStyle = (selected) => {
  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  return classNames(
    "w-full py-2.5 text-lg leading-5 font-medium  rounded-lg",
    selected
      ? "text-gray-100 bg-green-900 bg-opacity-50"
      : "text-blue-100 dark:hover:bg-white/[0.12] hover:text-white bg-gray-800"
  );
};

const MemberHours = () => {
  const [config, setConfig] = useState({
    memberHoursFundraisingTypes: [],
    memberHoursTrainingTypes: []
  });
  const [loadingError, setLoadingError] = useState(false);
  const [reportIsRunning, setReportIsRunning] = useState(false);
  const [year, setYear] = useState(DayJS().year());
  const [reportResults, setReportResults] = useState(false);

  const getConfig = () => {
    API.reports.memberHours.getConfig().then(res => {
      setConfig(res);
    }).catch(err => {
      setLoadingError("Unable to retrive report config from server.");
    })
  }

  useEffect(() => {
    getConfig();
  }, []);

  const pickableEvents = useMemo(() => {
    if(config.event_types === undefined) return [];
    return config.event_types.map(item => {
      return {
        key: item._id,
        value: item.name,
        text: item.name
      }
    })
  }, [config]);

  const pickableYears = useMemo(() => {
    return [
      DayJS().year(),
      DayJS().year() - 1,
      DayJS().year() - 2,
      DayJS().year() - 3,
      DayJS().year() - 4,
    ]
  }, [])

  const getReport = () => {
    setReportIsRunning(true);
    setReportResults(false);
    API.reports.memberHours.runReport(year).then(res => {
      setReportResults(res);
    }).catch(err => {

    }).finally(() => {
      setReportIsRunning(false);
    })
  }
  const onSubmit = (data) => {
    API.reports.memberHours.setConfig(data).then(() => {
      getConfig();
      Toast.success("Successfully updated.");
    }).catch(err => {
      console.error("Unable to save config", err);
    })
  };
  const handleCsvExport = () => {
    CommonFunctions.generateCSV(`Members Hours - ${year}`, [
      {csv_field: "First Name", database_field: "firstName"},
      {csv_field: "Surname", database_field: "lastName"},
      {csv_field: "Callsign", database_field: "callsign"},

      {csv_field: "Training Hours", database_field: "_trainingHours"},
      {csv_field: "Fundraising Hours", database_field: "_fundraisingHours"},
    ], reportResults.memberRecords.map(a => {
      return {
        ...a,
        _trainingHours: a._trainingHours,
        _fundraisingHours: a._fundraisingHours,
      }
    }));
  }

  return (
    <Layout full>
      <Tab.Group defaultIndex={0}>
        <Tab.List className="flex space-x-1 border-b border-zinc-200  p-1 pb-2 dark:border-zinc-700 dark:bg-blue-900/20">
          <Tab className={({selected}) => returnTabButtonStyle(selected)}>
            Report
          </Tab>
          <Tab className={({selected}) => returnTabButtonStyle(selected)}>
            Settings
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel className={"p-1"}>
            <div className={"flex gap-2"}>
              <div className={"w-20"}>
                <Select
                  disabled={reportIsRunning}
                  dontSort
                  options={pickableYears.map(year => {
                    return {
                      id: year,
                      text: "" + year,
                      value: year
                    }
                  })}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <button
                disabled={year === "" || reportIsRunning}
                onClick={getReport}
                className="h-10 cursor-pointer rounded-md border border-green-500 bg-green-300 bg-opacity-40 px-4 py-2 mt-1 text-green-500 hover:border-green-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Run Report
              </button>
            </div>

            {reportIsRunning ? <div>
              <div className="flex flex-wrap justify-center gap-4 p-4">
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
                <Skeleton className="h-20 w-1/4 bg-zinc-200 p-4 dark:bg-zinc-700"></Skeleton>
              </div>
            </div> : <>
              {reportResults && <div className={"mt-4 border-t py-2"}>
                {reportResults?.memberRecords?.length === 0 ? <div className={"text-center"}>No results to display.</div> : <Box title={"Results"} button={{
                  text: "CSV Export",
                  colourOverride: "text-sm text-white hover:text-gray-200 border",
                  action: () => handleCsvExport()
                }}>
                  <div className={"flex gap-2 p-2"}>
                    <Stat
                      label="Team Total Fundraising Hours"
                      value={reportResults.totalFundraisingHours}
                      colour="bg-green-300 bg-opacity-40"
                    />
                    <Stat
                      label="Team Total Training Hours"
                      value={reportResults.totalTrainingHours}
                      colour="bg-green-300 bg-opacity-40"
                    />
                  </div>

                  <table className={`tableClass`}>
                    <thead>
                    <tr className="tableHeadRowClass">
                      <th className="tableHeadCellClass">Name</th>
                      <th className="tableHeadCellClass w-40 text-center">Training Hours</th>
                      <th className="tableHeadCellClass w-40 text-center">Fundraising Hours</th>
                    </tr>
                    </thead>
                    <tbody className="tableBodyClass">{reportResults?.memberRecords?.map(item => {
                      return <tr key={item._id} className="tableBodyRowClass">
                        <td className="tableBodyCellClass">{item.firstName} {item.lastName}{" "}{item.callsign ? (<span className="italic">({item.callsign})</span>) : (<></>)}</td>
                        <td className="tableBodyCellClass text-center">{item._trainingHours}</td>
                        <td className="tableBodyCellClass text-center">{item._fundraisingHours}</td>
                      </tr>
                    })}</tbody>
                  </table>
                </Box>}
              </div>}
            </>}
          </Tab.Panel>
          <Tab.Panel className={"p-1"}>
            <div className={"pt-2 grid gap-4 grid-cols-2"}>
              <SettingsDropdownList
                title="Fundraising Event Types"
                field="memberHoursFundraisingTypes"
                items={pickableEvents.filter(a => config.memberHoursFundraisingTypes.indexOf(a.value) === -1)}
                data={config.memberHoursFundraisingTypes || []}
                onSubmit={onSubmit}
                onDelete={onSubmit}
              />
              <SettingsDropdownList
                title="Training Event Types"
                field="memberHoursTrainingTypes"
                items={pickableEvents.filter(a => config.memberHoursTrainingTypes.indexOf(a.value) === -1)}
                data={config.memberHoursTrainingTypes || []}
                onSubmit={onSubmit}
                onDelete={onSubmit}
              />
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </Layout>
  );
}
export default MemberHours;